import { Alert, Button, Collapse, Spin, Typography } from "antd";
import type { UseQueryResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { AnnouncementPreview } from "../app/announcements.ts";
import { formatDate } from "../app/classes.ts";

interface Props {
  query: UseQueryResult<AnnouncementPreview>;
}

/** How many chats an announcement would reach and who cannot be reached, from the server's preview. */
export function AnnouncementRecipientsPreview({ query }: Props) {
  const { t } = useTranslation();
  if (query.fetchStatus === "idle" && query.data === undefined) {
    return <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("announcements.preview.incomplete")} />;
  }

  if (query.isError) {
    return (
      <Alert
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
        title={t("announcements.preview.error")}
        action={<Button size="small" onClick={() => void query.refetch()}>{t("app.retry")}</Button>}
      />
    );
  }

  if (!query.data) {
    return <Spin style={{ display: "block", marginBottom: 16 }} />;
  }

  const preview = query.data;
  const notLinkedCount = Number(preview.notLinkedCount);
  const listed = preview.notLinked.length;
  return (
    <div aria-live="polite" style={{ marginBottom: 16 }}>
      <Alert
        type={Number(preview.linkedChats) > 0 ? "success" : "warning"}
        showIcon
        title={t("announcements.preview.count", { count: Number(preview.linkedChats), date: formatDate(preview.date) })}
        description={notLinkedCount > 0 ? t("announcements.preview.notLinked", { count: notLinkedCount }) : t("announcements.preview.allLinked")}
      />
      {notLinkedCount > 0 && (
        <Collapse
          size="small"
          style={{ marginTop: 8 }}
          items={[
            {
              key: "not-linked",
              label: t("announcements.preview.showNotLinked"),
              children: (
                <>
                  <ul style={{ margin: 0, paddingInlineStart: 20 }}>
                    {preview.notLinked.map((person) => (
                      <li key={`${person.kind}-${person.profileId}`}>
                        {person.fullName} <Typography.Text type="secondary">({t(`announcements.profileKinds.${person.kind}`)})</Typography.Text>
                      </li>
                    ))}
                  </ul>
                  {notLinkedCount > listed && <Typography.Text type="secondary">{t("announcements.preview.andMore", { count: notLinkedCount - listed })}</Typography.Text>}
                </>
              ),
            },
          ]}
        />
      )}
    </div>
  );
}
